import React, { useEffect, useState } from "react";
import { api, fmtMoney, fmtDate, STAGES, STAGE_PROBABILITY, CLOSED_LOST_REASONS, SUB_TYPES } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Phone, Calendar, MessageSquare, Mail, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import ProductCheckboxes from "@/components/ProductCheckboxes";

const ACTIVITY_TYPES = [
  { v: "call", label: "Call", icon: Phone, color: "text-blue-700 bg-blue-50 border-blue-200" },
  { v: "meeting", label: "Meeting", icon: Calendar, color: "text-violet-700 bg-violet-50 border-violet-200" },
  { v: "note", label: "Note", icon: MessageSquare, color: "text-slate-700 bg-slate-50 border-slate-200" },
  { v: "email", label: "Email", icon: Mail, color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
];

export default function DealEditDialog({ deal, onClose, onSaved }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [form, setForm] = useState(() => ({ ...deal }));
  const [saving, setSaving] = useState(false);
  const [activities, setActivities] = useState([]);
  const [actType, setActType] = useState("call");
  const [actNotes, setActNotes] = useState("");
  const [actDate, setActDate] = useState(() => new Date().toISOString().slice(0, 16));
  const [logging, setLogging] = useState(false);
  const [users, setUsers] = useState([]);
  const [staxCatalogue, setStaxCatalogue] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Fetch full deal in case caller passed a partial object (e.g. from forecast endpoint)
        const r = await api.get(`/deals/${deal.id}`);
        if (!cancelled) setForm((prev) => {
          const merged = { ...r.data, ...Object.fromEntries(Object.entries(prev).filter(([, v]) => v !== undefined && v !== null && v !== "")) };
          // Backfill stax_products from the legacy product_items array so
          // older deals created before this field existed still save cleanly.
          if ((!Array.isArray(merged.stax_products) || merged.stax_products.length === 0) && Array.isArray(merged.product_items)) {
            merged.stax_products = merged.product_items.map((p) => p.name);
          }
          return merged;
        });
      } catch (_) { /* ignore — keep initial form */ }
      try {
        const r = await api.get(`/deals/${deal.id}/activities`);
        if (!cancelled) setActivities(r.data);
      } catch (_) { /* ignore */ }
      try {
        // Pull both STAX catalogues (newer `stax-services` + legacy
        // `stax-products`) and merge by name so the picker reflects every
        // option a deal could have been tagged with historically.
        const [svcs, prods] = await Promise.allSettled([
          api.get("/stax-services"),
          api.get("/stax-products"),
        ]);
        if (!cancelled) {
          const seen = new Set();
          const merged = [];
          [svcs, prods].forEach((r) => {
            if (r.status !== "fulfilled") return;
            (r.value.data || []).forEach((p) => {
              if (!p?.name || seen.has(p.name)) return;
              seen.add(p.name);
              merged.push(p);
            });
          });
          setStaxCatalogue(merged);
        }
      } catch (_) { /* ignore */ }
      if (isAdmin) {
        try {
          const u = await api.get("/users");
          if (!cancelled) setUsers(u.data);
        } catch (_) { /* ignore */ }
      }
    })();
    return () => { cancelled = true; };
  }, [deal.id, isAdmin]);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        stage: form.stage,
        revenue: Number(form.revenue) || 0,
        cost: Number(form.cost) || 0,
        term_months: Number(form.term_months) || 12,
        close_date: form.close_date || null,
        owner_name: form.owner_name || null,
        subscription_type: form.subscription_type || null,
        notes: form.notes || null,
        licence_count: form.licence_count === "" || form.licence_count == null ? null : Number(form.licence_count),
        stax_products: Array.isArray(form.stax_products) ? form.stax_products : [],
        // Only send closed_lost_reason when the stage is Closed Lost; otherwise
        // send null explicitly so the backend clears any stale value.
        closed_lost_reason: form.stage === "Closed Lost" ? (form.closed_lost_reason || null) : null,
        closed_lost_notes: form.stage === "Closed Lost" ? (form.closed_lost_notes || "") : "",
        // Manual uplift MRR entered by the deal owner. Counts toward their
        // sales target as new MRR. Send null to clear.
        manual_uplift_mrr: form.manual_uplift_mrr === "" || form.manual_uplift_mrr == null
          ? null
          : Number(form.manual_uplift_mrr),
      };
      if (!payload.stax_products || payload.stax_products.length === 0) {
        toast.error("Pick at least one STAX product");
        setSaving(false);
        return;
      }
      if (isAdmin && form.owner_id) payload.owner_id = form.owner_id;
      await api.patch(`/deals/${deal.id}`, payload);
      toast.success("Deal updated");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update deal");
    } finally {
      setSaving(false);
    }
  };

  const logActivity = async () => {
    if (!actNotes.trim()) return toast.error("Add some notes for the activity");
    setLogging(true);
    try {
      const r = await api.post(`/deals/${deal.id}/activities`, {
        type: actType,
        notes: actNotes.trim(),
        occurred_at: new Date(actDate).toISOString(),
      });
      setActivities((prev) => [r.data, ...prev]);
      setActNotes("");
      toast.success(`${ACTIVITY_TYPES.find((t) => t.v === actType)?.label} logged`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to log");
    } finally {
      setLogging(false);
    }
  };

  const deleteActivity = async (id) => {
    if (!window.confirm("Delete this activity?")) return;
    try {
      await api.delete(`/deals/${deal.id}/activities/${id}`);
      setActivities((prev) => prev.filter((a) => a.id !== id));
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="deal-edit-dialog">
        <DialogHeader>
          <DialogTitle>
            <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-blue-700">
              {deal.account_name} · {deal.id}
            </div>
            <div className="font-heading text-xl font-bold text-slate-900 mt-1">Edit deal</div>
          </DialogTitle>
        </DialogHeader>

        {/* Edit form */}
        <div className="space-y-3 py-1">
          <div>
            <Label className="text-xs">Deal name</Label>
            <Input data-testid="deal-edit-name" value={form.name || ""} onChange={(e) => setField("name", e.target.value)} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Stage</Label>
              <Select value={form.stage} onValueChange={(v) => {
                // Auto-sync probability to match the selected stage — mirrors
                // the AccountDetail form, and keeps the UI consistent with
                // the backend's stage_changed logic. Engineer can still
                // override % manually afterwards.
                setForm((p) => ({
                  ...p,
                  stage: v,
                  probability: STAGE_PROBABILITY[v] ?? p.probability,
                }));
              }}>
                <SelectTrigger data-testid="deal-edit-stage"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STAGES.map((s) => <SelectItem key={s} value={s}>{s}{STAGE_PROBABILITY[s] != null ? ` · ${STAGE_PROBABILITY[s]}%` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Type</Label>
              <Select
                value={form.subscription_type || "New Business"}
                onValueChange={(v) => setField("subscription_type", v)}
              >
                <SelectTrigger data-testid="deal-edit-subscription-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SUB_TYPES.map((s) => <SelectItem key={s} value={s} data-testid={`deal-edit-subscription-type-opt-${s.replace(/\s+/g, "-").toLowerCase()}`}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Licence count</Label>
              <Input
                data-testid="deal-edit-licence-count"
                type="number"
                min="0"
                step="1"
                placeholder="e.g. 250"
                value={form.licence_count ?? ""}
                onChange={(e) => setField("licence_count", e.target.value)}
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Revenue £</Label>
              <Input type="number" value={form.revenue ?? ""} onChange={(e) => setField("revenue", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Cost £</Label>
              <Input type="number" value={form.cost ?? ""} onChange={(e) => setField("cost", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Probability (auto)</Label>
              <div
                data-testid="deal-edit-probability"
                className="h-10 flex items-center px-3 rounded-md border border-slate-200 bg-slate-50 text-sm font-mono text-slate-700"
                title="Probability is derived automatically from the chosen stage"
              >
                {form.probability ?? 0}%
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Term (months)</Label>
              <Input type="number" value={form.term_months ?? ""} onChange={(e) => setField("term_months", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Close date</Label>
              <Input type="date" value={form.close_date ? form.close_date.slice(0, 10) : ""} onChange={(e) => setField("close_date", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Owner {isAdmin ? "" : "(admin to change)"}</Label>
              {isAdmin && users.length > 0 ? (
                <Select
                  value={form.owner_id || "__none__"}
                  onValueChange={(v) => {
                    if (v === "__none__") { setField("owner_id", ""); setField("owner_name", ""); return; }
                    const u = users.find((x) => x.user_id === v);
                    setField("owner_id", v);
                    setField("owner_name", u?.name || u?.email || "");
                  }}
                >
                  <SelectTrigger data-testid="deal-edit-owner"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Unassigned</SelectItem>
                    {users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name} · {u.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input value={form.owner_name || ""} disabled={!isAdmin} onChange={(e) => setField("owner_name", e.target.value)} />
              )}
            </div>
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea rows={2} value={form.notes || ""} onChange={(e) => setField("notes", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">STAX products <span className="text-slate-400">(at least one is required)</span></Label>
            <div className="mt-1.5 border border-slate-200 rounded-md p-3 bg-slate-50/60 max-h-64 overflow-y-auto" data-testid="deal-edit-stax">
              <ProductCheckboxes
                products={staxCatalogue}
                selected={Array.isArray(form.stax_products) ? form.stax_products : (form.product_items || []).map((p) => p.name)}
                onChange={(names) => setField("stax_products", names)}
                testidPrefix="deal-edit-stax"
              />
            </div>
          </div>
          {/* Manual uplift MRR — credited to the deal owner's sales target */}
          <div data-testid="manual-uplift-block" className="bg-emerald-50/40 border border-emerald-100 rounded-md p-3">
            <Label className="text-xs text-emerald-800 font-medium">
              Uplift MRR (£) <span className="text-slate-400 font-normal">(optional)</span>
            </Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              placeholder="e.g. 250 — for the MRR increase you secured over the previous term"
              value={form.manual_uplift_mrr ?? ""}
              onChange={(e) => setField("manual_uplift_mrr", e.target.value)}
              data-testid="deal-edit-manual-uplift"
              className="mt-1 bg-white"
            />
            <div className="text-[10px] text-emerald-700/80 mt-1 font-mono">
              Enter the incremental MRR you earned on this deal. Credits your Sales Target as new MRR.
              Leave blank if there&apos;s no uplift.
            </div>
          </div>
          {form.stage === "Closed Lost" && (
            <div data-testid="closed-lost-reason-block" className="bg-rose-50/40 border border-rose-100 rounded-md p-3">
              <Label className="text-xs text-rose-800 font-medium">Closed Lost reason (optional)</Label>
              <Select
                value={form.closed_lost_reason || "__none__"}
                onValueChange={(v) => setField("closed_lost_reason", v === "__none__" ? "" : v)}
              >
                <SelectTrigger data-testid="deal-edit-closed-lost-reason" className="mt-1 bg-white">
                  <SelectValue placeholder="— None —" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— None —</SelectItem>
                  {CLOSED_LOST_REASONS.map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-[10px] text-rose-700/70 mt-1 font-mono">
                Helps track why deals fall away — surfaced in lost-analysis reports.
              </div>
              <div className="mt-3">
                <Label className="text-xs text-rose-800 font-medium">Closed Lost notes (optional)</Label>
                <Textarea
                  rows={2}
                  className="mt-1 bg-white"
                  value={form.closed_lost_notes || ""}
                  onChange={(e) => setField("closed_lost_notes", e.target.value)}
                  placeholder="Any extra detail — who they went with, the specific blocker, etc."
                  data-testid="deal-edit-closed-lost-notes"
                />
              </div>
            </div>
          )}
          <DialogFooter className="pt-2 border-b border-slate-100 pb-3">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button data-testid="deal-edit-save" onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 gap-1.5">
              <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </div>

        {/* Activity log */}
        <div className="space-y-3 pt-3">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-blue-700">Activity log</div>
            <div className="font-heading text-base font-bold text-slate-900 mt-0.5">Calls · Meetings · Notes</div>
          </div>

          {/* Logger */}
          <div className="border border-slate-200 rounded-md p-3 bg-slate-50 space-y-2">
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-[10px] font-mono uppercase text-slate-500">Type</Label>
                <Select value={actType} onValueChange={setActType}>
                  <SelectTrigger data-testid="deal-activity-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ACTIVITY_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] font-mono uppercase text-slate-500">When</Label>
                <Input type="datetime-local" value={actDate} onChange={(e) => setActDate(e.target.value)} />
              </div>
            </div>
            <div>
              <Label className="text-[10px] font-mono uppercase text-slate-500">Notes</Label>
              <Textarea
                data-testid="deal-activity-notes"
                rows={2}
                value={actNotes}
                onChange={(e) => setActNotes(e.target.value)}
                placeholder="What happened? Action items, sentiment, follow-ups…"
              />
            </div>
            <div className="flex justify-end">
              <Button
                data-testid="deal-activity-log"
                onClick={logActivity}
                disabled={logging}
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 gap-1.5"
              >
                Log {ACTIVITY_TYPES.find((t) => t.v === actType)?.label}
              </Button>
            </div>
          </div>

          {/* History */}
          <div className="space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="deal-activities-list">
            {activities.length === 0 ? (
              <div className="text-xs text-slate-400 text-center py-6">
                No activity yet — log the first call/meeting/note above.
              </div>
            ) : (
              activities.map((a) => {
                const t = ACTIVITY_TYPES.find((x) => x.v === a.type) || ACTIVITY_TYPES[2];
                const Icon = t.icon;
                return (
                  <div key={a.id} className="flex gap-3 p-3 border border-slate-200 rounded-md hover:bg-slate-50 group">
                    <div className={`h-7 w-7 flex-shrink-0 rounded-md border ${t.color} flex items-center justify-center`}>
                      <Icon className="h-3.5 w-3.5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 text-xs">
                          <Badge variant="outline" className={`${t.color} text-[10px] py-0 px-1.5`}>{t.label}</Badge>
                          <span className="text-slate-500">
                            {new Date(a.occurred_at).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                          </span>
                          <span className="text-slate-400">·</span>
                          <span className="text-slate-500">{a.created_by_name}</span>
                        </div>
                        <button
                          onClick={() => deleteActivity(a.id)}
                          className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-600 transition"
                          title="Delete"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <div className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{a.notes}</div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
